import useDiscoveryFetch from './useDiscoveryFetch';
import { useState, useEffect, useMemo, memo } from 'react';
import api from '../../api/axiosInstance';
import { useParams } from 'react-router-dom';
import {
    Container,
    Row,
    Col,
    Card,
    Image,
    Spinner,
    Alert,
    OverlayTrigger,
    Form,
} from 'react-bootstrap';
import Tooltip from 'react-bootstrap/Tooltip';
import Sidebar from '../../Sidebar';
import Breadcrumb from '../../Breadcrumb';
import NavTabs from '../../NavTabs';
import { useMobileAiPageContext } from '../../MobileAiAssistantContext';
import { getDiscoveryAiSuggestions } from '../../utils/discoveryAiSuggestions';
import currencies from '../../data/currencies';
import icCity from '../../assets/img/ic-city.svg';
import icNotes from '../../assets/img/ic-news.svg';
import icInfo from '../../assets/img/ic-info-circle.svg';
import icGraphRise from '../../assets/img/ic-graph-rise.svg';
import { trackEvent } from '../../services/analytics/analyticsService';
import { ANALYTICS_EVENTS } from '../../services/analytics/analyticsEvents';
import { useMediaQuery } from 'react-responsive';
import CityDetailHero from './cityDetail/CityDetailHero';
import CityDetailMobileTabs from './cityDetail/CityDetailMobileTabs';

const toStr = (v) => (v !== null && v !== undefined ? String(v) : '');

const toDigits = (val) => {
    if (val === null || val === undefined) return '';
    return String(val).replace(/[^\d]/g, '');
};

const formatThousands = (digits) => {
    if (!digits) return '';
    return String(digits).replace(/\B(?=(\d{3})+(?!\d))/g, "'");
};

const RangeCell = memo(function RangeCell({ name, value, onChange, disabled, adornment, className = '' }) {
    const [local, setLocal] = useState(() => toDigits(value));

    useEffect(() => {
        setLocal(toDigits(value));
    }, [value]);

    const handleLocalChange = (e) => {
        setLocal(toDigits(e.target.value));
    };

    const handleBlur = () => {
        const digits = local || '';
        if (digits !== toDigits(value ?? '')) {
            onChange({ target: { name, value: digits } });
        }
    };

    return (
        <div className={`as-table d-flex align-items-center justify-between ${className}`}>
            <Form.Control
                type="text"
                inputMode="numeric"
                autoComplete="off"
                spellCheck={false}
                name={name}
                value={formatThousands(local)}
                onChange={handleLocalChange}
                onBlur={handleBlur}
                disabled={disabled}
                onWheel={(e) => e.currentTarget.blur()}
                style={{ textAlign: 'right', flex: 1 }}
            />
            {adornment ? <span aria-hidden="true" className="mx-1">{adornment}</span> : null}
        </div>
    );
});

const normalizeUrl = (raw) => {
    const s = (raw || '').trim();
    if (!s) return null;
    const withScheme = /^[a-zA-Z][a-zA-Z0-9+\-.]*:/.test(s) ? s : `https://${s}`;
    try {
        const u = new URL(withScheme);
        if (!['http:', 'https:'].includes(u.protocol)) return null;
        return u.toString();
    } catch {
        return null;
    }
};

const SalarySectionWrapper = ({ mobile, children }) => {
    if (!mobile) return children;

    return (
        <Card className="company-detail-section mb-4 city-salary-section">
            <Card.Body>{children}</Card.Body>
        </Card>
    );
};

const MobileSalaryMatrix = ({ bands, pillars, formData, currencySymbol }) => {
    const regularBands = bands.filter((band) => band.prefix !== 'uplift');
    const upliftBand = bands.find((band) => band.prefix === 'uplift');
    const displayValue = (value, adornment) => `${formatThousands(toDigits(value)) || '—'} ${adornment}`;

    return (
        <div className="mt-4 mobile-salary-matrix">
            {regularBands.map((band) => (
                <section className="mobile-salary-band" key={band.prefix || 'student'}>
                    <h4 className="mobile-salary-band__title">{band.label}</h4>
                    {pillars.map((pillar) => {
                        const nameFrom = fieldName(band.prefix, pillar.key, 'From');
                        const nameTo = fieldName(band.prefix, pillar.key, 'To');

                        return (
                            <div className="mobile-salary-pillar" key={pillar.key}>
                                <h5 className="mobile-salary-pillar__title">
                                    <span>{pillar.label}</span>
                                    <OverlayTrigger placement="top" overlay={<Tooltip>Info text here</Tooltip>}>
                                        <Image src={icInfo} alt="Additional Info" />
                                    </OverlayTrigger>
                                </h5>
                                <div className="mobile-salary-range">
                                    <div className="mobile-salary-range__labels"><span>From</span><span>To</span></div>
                                    <div className="mobile-salary-range__values">
                                        <span>{displayValue(formData[nameFrom], currencySymbol)}</span>
                                        <span>{displayValue(formData[nameTo], currencySymbol)}</span>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </section>
            ))}

            {upliftBand && (
                <section className="mobile-salary-band mobile-salary-band--uplift">
                    <h4 className="mobile-salary-band__title">{upliftBand.label}</h4>
                    {pillars.map((pillar) => {
                        const field = fieldName(upliftBand.prefix, pillar.key);
                        return (
                            <div className="icon-text mobile-salary-uplift" key={pillar.key}>
                                <span className="icon" aria-hidden="true"><Image src={icGraphRise} alt="" /></span>
                                <span className="text">{pillar.label}<br /><strong>{displayValue(formData[field], '%')}</strong></span>
                            </div>
                        );
                    })}
                </section>
            )}
        </div>
    );
};

const pillars = [
    { key: 'central',    label: 'Central functions' },
    { key: 'production', label: 'Operations/production' },
    { key: 'it',         label: 'IT' },
];

const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);

const fieldName = (bandPrefix, pillarKey, side) =>
    bandPrefix === 'uplift'
        ? `${bandPrefix}${cap(pillarKey)}`
        : `${bandPrefix}${bandPrefix ? cap(pillarKey) : pillarKey}${side}`;

const DiscoveryCityViewSalaries = () => {
    const isMobile = useMediaQuery({ query: '(max-width: 991.98px)' });
    const { id, cityId: nestedCityId } = useParams();
    const cityId = nestedCityId || id;
    const countryId = nestedCityId ? id : null;

    const [cityData, setCityData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useDiscoveryFetch(({ signal, isCurrent, isReconnect }) => {
        const fetchCity = async () => {
            try {
                if (!isReconnect || !cityData) setLoading(true);
                setError('');
                const response = await api.get(`/api/discovery/cities/${cityId}/salary`, { signal });
                if (!isCurrent()) return;
                setCityData(response.data);

                if (!isReconnect) trackEvent(ANALYTICS_EVENTS.DISCOVERY_CITY_SALARY_VIEWED, {
                    cityId: response.data?._id || cityId,
                    cityName: response.data?.name || response.data?.cityName || '',
                    countryCode: String(response.data?.country?.id || response.data?.country?.iso2 || countryId || '').toUpperCase(),
                    countryName: response.data?.country?.name || '',
                    level: 3,
                    source: 'city_salary_tab',
                });
            } catch (err) {
                if (!isCurrent()) return;
                console.error('Error fetching city salary:', err);
                setError('An error occurred while fetching city salary details.');
            } finally {
                if (!isCurrent()) return;
                setLoading(false);
            }
        };

        fetchCity();
    }, [cityId, countryId]);

    const student  = Array.isArray(cityData?.studentJobs)  ? cityData.studentJobs[0]  : {};
    const intern   = Array.isArray(cityData?.internJobs)    ? cityData.internJobs[0]   : {};
    const oneThree = Array.isArray(cityData?.oneThreeJobs)  ? cityData.oneThreeJobs[0] : {};
    const three    = Array.isArray(cityData?.threeJobs)     ? cityData.threeJobs[0]    : {};
    const uplift   = Array.isArray(cityData?.uplift)        ? cityData.uplift[0]       : {};

    const formData = {
        currency:  cityData?.currency  || '',
        govUrl:    cityData?.govUrl    || '',
        legalUrl:  cityData?.legalUrl  || '',
        remarks:   cityData?.remarks   || '',

        centralFrom:     toStr(student?.centralFrom),
        centralTo:       toStr(student?.centralTo),
        productionFrom:  toStr(student?.productionFrom),
        productionTo:    toStr(student?.productionTo),
        itFrom:          toStr(student?.itFrom),
        itTo:            toStr(student?.itTo),

        internCentralFrom:    toStr(intern?.internCentralFrom),
        internCentralTo:      toStr(intern?.internCentralTo),
        internProductionFrom: toStr(intern?.internProductionFrom),
        internProductionTo:   toStr(intern?.internProductionTo),
        internItFrom:         toStr(intern?.internItFrom),
        internItTo:           toStr(intern?.internItTo),

        oneThreeCentralFrom:    toStr(oneThree?.oneThreeCentralFrom),
        oneThreeCentralTo:      toStr(oneThree?.oneThreeCentralTo),
        oneThreeProductionFrom: toStr(oneThree?.oneThreeProductionFrom),
        oneThreeProductionTo:   toStr(oneThree?.oneThreeProductionTo),
        oneThreeItFrom:         toStr(oneThree?.oneThreeItFrom),
        oneThreeItTo:           toStr(oneThree?.oneThreeItTo),

        threeCentralFrom:    toStr(three?.threeCentralFrom),
        threeCentralTo:      toStr(three?.threeCentralTo),
        threeProductionFrom: toStr(three?.threeProductionFrom),
        threeProductionTo:   toStr(three?.threeProductionTo),
        threeItFrom:         toStr(three?.threeItFrom),
        threeItTo:           toStr(three?.threeItTo),

        upliftCentral:    toStr(uplift?.upliftCentral),
        upliftProduction: toStr(uplift?.upliftProduction),
        upliftIt:         toStr(uplift?.upliftIt),
    };

    const currencySymbol = useMemo(() => {
        const code = formData.currency || 'EUR';
        const entry = Array.isArray(currencies)
            ? currencies.find(c => (typeof c === 'string' ? c === code : c?.value === code))
            : null;
        const label = typeof entry === 'string' ? entry : entry?.label;
        if (typeof label === 'string' && label.length) {
            const beforeDash = label.split(' - ')[0].trim();
            return beforeDash || code;
        }
        return (typeof entry === 'string' ? entry : entry?.value) || code || '€';
    }, [formData.currency]);

    const safeUrlHref = normalizeUrl(cityData?.url);
    const govHref   = normalizeUrl(formData.govUrl);
    const legalHref = normalizeUrl(formData.legalUrl);

    const bands = [
        {
            label: <>Student jobs <span className="label-sub">/ Hourly</span>
                <OverlayTrigger placement="top" overlay={<Tooltip>Info text here</Tooltip>}>
                    <Image src={icInfo} alt="Additional Info" />
                </OverlayTrigger>
            </>,
            prefix: '',
        },
        {
            label: <>Intern jobs <span className="label-sub">/ Hourly</span>
                <OverlayTrigger placement="top" overlay={<Tooltip>Info text here</Tooltip>}>
                    <Image src={icInfo} alt="Additional Info" />
                </OverlayTrigger>
            </>,
            prefix: 'intern',
        },
        {
            label: <>1–3 year jobs <span className="label-sub">/ Hourly</span>
                <OverlayTrigger placement="top" overlay={<Tooltip>Info text here</Tooltip>}>
                    <Image src={icInfo} alt="Additional Info" />
                </OverlayTrigger>
            </>,
            prefix: 'oneThree',
        },
        {
            label: <>3+ year jobs <span className="label-sub">/ Hourly</span>
                <OverlayTrigger placement="top" overlay={<Tooltip>Info text here</Tooltip>}>
                    <Image src={icInfo} alt="Additional Info" />
                </OverlayTrigger>
            </>,
            prefix: 'three',
        },
        {
            label: <>Uplift from Top Payers <span className="label-sub">/ Hourly</span>
                <OverlayTrigger placement="top" overlay={<Tooltip>Info text here</Tooltip>}>
                    <Image src={icInfo} alt="Additional Info" />
                </OverlayTrigger>
            </>,
            prefix: 'uplift',
            isPercent: true,
        },
    ];

    const cityBasePath = countryId
        ? `/discovery/countries/${countryId}/cities/${cityId}`
        : `/discovery/cities/${cityId}`;
    const discoveryCityNavItems = [
        { to: cityBasePath, eventKey: 'about', label: 'General Info', end: true },
        { to: `${cityBasePath}/jobs`, eventKey: 'companies', label: 'Companies & Jobs', alsoActive: [`${cityBasePath}/companies`] },
        { to: `${cityBasePath}/salary`, eventKey: 'salary', label: 'Average salaries' },
        { to: `${cityBasePath}/insights`, eventKey: 'insights', label: 'City insights' },
        { to: `${cityBasePath}/universities`, eventKey: 'universities', label: 'Universities' },
        { to: `${cityBasePath}/comments`, eventKey: 'comments', label: 'Comments' },
    ];

    // Opt this page into the shared mobile AI assistant (header trigger + bottom sheet).
    useMobileAiPageContext({
        pageType: 'discovery-city',
        discoveryType: 'cities',
        cityId,
        cityName: cityData?.name || cityData?.cityName || '',
        countryName: cityData?.country?.name || '',
        activeTab: 'Average Salaries',
        suggestions: getDiscoveryAiSuggestions({
            discoveryType: 'cities',
            selectedCity: cityData?.name || cityData?.cityName || '',
            selectedCountry: cityData?.country?.name || '',
        }),
    });

    if (loading) {
        return (
            <>
                <Sidebar />
                <main className="main-content flex-grow-1">
                    <Container>
                        <Row>
                            <Col className="py-5 text-center">
                                <Spinner animation="border" />
                            </Col>
                        </Row>
                    </Container>
                </main>
            </>
        );
    }

    if (error) {
        return (
            <>
                <Sidebar />
                <main className="main-content flex-grow-1">
                    <Container>
                        <Row>
                            <Col>
                                <Alert variant="danger" className="mt-4">{error}</Alert>
                            </Col>
                        </Row>
                    </Container>
                </main>
            </>
        );
    }

    if (!cityData) {
        return (
            <>
                <Sidebar />
                <main className="main-content flex-grow-1">
                    <Container>
                        <Row>
                            <Col>
                                <Alert variant="warning" className="mt-4">City not found.</Alert>
                            </Col>
                        </Row>
                    </Container>
                </main>
            </>
        );
    }

    return (
        <>
            <Sidebar />
            <main className="main-content flex-grow-1">
                <Container>
                    <Row>
                        <Col>
                            <Breadcrumb cityName={cityData?.name} subPage="Average Salaries" fromCountry={countryId} countryName={cityData?.country?.name} />
                        </Col>
                    </Row>

                    <Row className={isMobile ? 'g-0 country-detail-mobile city-detail-mobile' : undefined}>
                        <Col className={isMobile ? 'p-0' : undefined}>
                            <Card className={isMobile ? 'border-0 bg-transparent mb-0' : 'mb-4'}>
                                <Card.Body className={isMobile ? 'p-0' : 'pt-0'}>
                                    <Row className={isMobile ? 'g-0' : undefined}>
                                        <Col xs={12} className={isMobile ? 'p-0' : undefined}>
                                            {isMobile ? (
                                                <CityDetailHero
                                                    cityName={cityData.name}
                                                    url={safeUrlHref ? { href: safeUrlHref, label: cityData.url } : null}
                                                    countryName={cityData.country?.name}
                                                />
                                            ) : (
                                            <Row>
                                                <Col>
                                                    <Card className="card-premium">
                                                        <Card.Body>
                                                            <Row className="align-items-center">
                                                                <Col>
                                                                    <h3 className="text-start">City</h3>
                                                                    <hr />
                                                                </Col>
                                                            </Row>
                                                            <Row className="mb-4">
                                                                <Col md={8} className="d-flex">
                                                                    <div>
                                                                        <h1>{cityData.name || '—'}</h1>
                                                                        <p>
                                                                            {safeUrlHref ? (
                                                                                <a href={safeUrlHref} target="_blank" rel="noopener noreferrer">
                                                                                    {cityData.url}
                                                                                </a>
                                                                            ) : cityData.url ? cityData.url : '—'}
                                                                            {cityData.country?.name && (
                                                                                <>
                                                                                    <span className="dot"></span>
                                                                                    Country: <strong>{cityData.country.name}</strong>
                                                                                </>
                                                                            )}
                                                                        </p>
                                                                    </div>
                                                                </Col>
                                                            </Row>
                                                        </Card.Body>
                                                    </Card>
                                                </Col>
                                            </Row>
                                            )}

                                            {isMobile ? (
                                                <CityDetailMobileTabs items={discoveryCityNavItems} />
                                            ) : (
                                            <Row className="mb-5">
                                                <Col>
                                                    <NavTabs items={discoveryCityNavItems} />
                                                </Col>
                                            </Row>
                                            )}

                                            {/* Salary card */}
                                            <Card className={isMobile ? 'country-detail-content-card mb-4' : 'mb-4'}>
                                                <Card.Body>
                                                    <SalarySectionWrapper mobile={isMobile}>
                                                    <Row className="align-items-center">
                                                        <Col>
                                                            <h3 className={isMobile ? 'company-detail-section-title' : undefined}>
                                                                Average salaries
                                                            </h3>
                                                            <hr />
                                                        </Col>
                                                    </Row>

                                                    {/* Meta fields */}
                                                    <Row>
                                                        <Col md={6}>
                                                            <Row className={isMobile ? 'mb-3' : 'mb-4'}>
                                                                <Col md={12}>
                                                                    <div className="icon-text">
                                                                        <span className="icon" aria-hidden="true">
                                                                            <Image src={icCity} alt="Currency" />
                                                                        </span>
                                                                        <span className="text">
                                                                            Currency
                                                                            <br />
                                                                            <strong>
                                                                                {formData.currency || 'N/A'}
                                                                            </strong>
                                                                        </span>
                                                                    </div>
                                                                </Col>
                                                            </Row>
                                                            <Row className={isMobile ? 'mb-3' : 'mb-4'}>
                                                                <Col md={12}>
                                                                    <div className="icon-text">
                                                                        <span className="icon" aria-hidden="true">
                                                                            <Image src={icCity} alt="Governmental tools URL" />
                                                                        </span>
                                                                        <span className="text">
                                                                            <span className="d-flex">
                                                                                Governmental tools URL
                                                                                <OverlayTrigger placement="top" overlay={<Tooltip>Info text here</Tooltip>}>
                                                                                    <Image src={icInfo} alt="Additional Info" className="ms-2" />
                                                                                </OverlayTrigger>
                                                                            </span>
                                                                            <strong>
                                                                                {govHref ? (
                                                                                    <a href={govHref} target="_blank" rel="noopener noreferrer">{formData.govUrl}</a>
                                                                                ) : 'N/A'}
                                                                            </strong>
                                                                        </span>
                                                                    </div>
                                                                </Col>
                                                            </Row>
                                                            <Row className={isMobile ? undefined : 'mb-4'}>
                                                                <Col md={12}>
                                                                    <div className="icon-text">
                                                                        <span className="icon" aria-hidden="true">
                                                                            <Image src={icCity} alt="Legal Employment conditions by law URL" />
                                                                        </span>
                                                                        <span className="text">
                                                                            <span className="d-flex">
                                                                                Legal Employment conditions by law URL
                                                                                <OverlayTrigger placement="top" overlay={<Tooltip>Info text here</Tooltip>}>
                                                                                    <Image src={icInfo} alt="Additional Info" className="ms-2" />
                                                                                </OverlayTrigger>
                                                                            </span>
                                                                            <strong>
                                                                                {legalHref ? (
                                                                                    <a href={legalHref} target="_blank" rel="noopener noreferrer">{formData.legalUrl}</a>
                                                                                ) : 'N/A'}
                                                                            </strong>
                                                                        </span>
                                                                    </div>
                                                                </Col>
                                                            </Row>
                                                        </Col>
                                                        <Col md={6}>
                                                            <div className="icon-text">
                                                                <span className="icon" aria-hidden="true">
                                                                    <Image src={icNotes} alt="Remarks" />
                                                                </span>
                                                                <span className="text">
                                                                    Remarks
                                                                    <br />
                                                                    <strong>
                                                                        {formData.remarks || 'N/A'}
                                                                    </strong>
                                                                </span>
                                                            </div>
                                                        </Col>
                                                    </Row>

                                                    {isMobile ? (
                                                        <MobileSalaryMatrix
                                                            bands={bands}
                                                            pillars={pillars}
                                                            formData={formData}
                                                            currencySymbol={currencySymbol}
                                                        />
                                                    ) : (
                                                    <>
                                                    {/* Header with 3 pillar cards & From/To sub-headers */}
                                                    <Row className="align-items-end">
                                                        <Col md={3} />
                                                        {pillars.map((p) => (
                                                            <Col key={p.key} md={3}>
                                                                <div className="salary-table__title">
                                                                    <span>{p.label}</span>
                                                                    <OverlayTrigger
                                                                        placement="top"
                                                                        overlay={<Tooltip>Info text here</Tooltip>}
                                                                    >
                                                                        <Image src={icInfo} alt="Additional Info" />
                                                                    </OverlayTrigger>
                                                                </div>
                                                                <div className="salary-table">
                                                                    <div className="salary-table__head">
                                                                        <div>From</div>
                                                                        <div>To</div>
                                                                    </div>
                                                                </div>
                                                            </Col>
                                                        ))}
                                                    </Row>

                                                    {/* Rows for each band */}
                                                    {bands.map((band, idx) => (
                                                        <Row
                                                            key={`${band.prefix ?? 'student'}-${idx}`}
                                                            className={isMobile ? (idx < bands.length - 1 ? 'mb-3' : undefined) : (idx === bands.length - 1 ? 'mb-4' : 'mb-0')}
                                                        >
                                                            <Col md={3} className="labels-column">
                                                                {band.label}
                                                            </Col>

                                                            {pillars.map((p) => {
                                                                const isUplift = band.prefix === 'uplift';
                                                                const nameFrom = isUplift
                                                                    ? fieldName(band.prefix, p.key)
                                                                    : fieldName(band.prefix, p.key, 'From');
                                                                const nameTo = isUplift ? null : fieldName(band.prefix, p.key, 'To');

                                                                return (
                                                                    <Col key={p.key} md={3}>
                                                                        <div className="salary-table__grid">
                                                                            <div className={
                                                                                'salary-table__cell' + (isUplift ? ' salary-table__cell--span2' : '')
                                                                            }>
                                                                                <RangeCell
                                                                                    name={nameFrom}
                                                                                    value={formData[nameFrom]}
                                                                                    onChange={() => {}}
                                                                                    disabled={true}
                                                                                    adornment={isUplift ? '%' : currencySymbol}
                                                                                />
                                                                            </div>

                                                                            {!isUplift && (
                                                                                <div className="salary-table__cell">
                                                                                    <RangeCell
                                                                                        name={nameTo}
                                                                                        value={formData[nameTo]}
                                                                                        onChange={() => {}}
                                                                                        disabled={true}
                                                                                        adornment={currencySymbol}
                                                                                    />
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </Col>
                                                                );
                                                            })}
                                                        </Row>
                                                    ))}
                                                    </>
                                                    )}
                                                    </SalarySectionWrapper>
                                                </Card.Body>
                                            </Card>

                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </main>
        </>
    );
};

export default DiscoveryCityViewSalaries;
